import { useMemo, useState, type FormEvent } from 'react'
import { AlertTriangle, ChevronDown, Search } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn, formatEventTime, formatNumber } from '@/lib/utils'
import type { StatusTone } from '@/lib/status-tones'
import { coverageLabel, formatRateFraction, formatTaxRate, STATE_NAMES } from './tax-format'
import {
  taxErrorMessage,
  useActivateTaxDataset,
  useImportTaxDataset,
  useTaxDataset,
  useTaxDatasets,
  useTaxDeliveries,
  useTaxLookup,
  type TaxDataset,
  type TaxDatasetDetail,
  type TaxDelivery,
} from './tax-rates-api'

type MonthRow = {
  version: string
  month: string
  dataset?: TaxDataset
  delivery?: TaxDelivery
}

const STATUS: Record<TaxDataset['status'], { label: string; tone: StatusTone }> = {
  active: { label: 'In use', tone: 'success' },
  imported: { label: 'Ready to review', tone: 'progress' },
  retired: { label: 'Replaced', tone: 'neutral' },
}

function monthRows(datasets: TaxDataset[], deliveries: TaxDelivery[]): MonthRow[] {
  const rows = new Map<string, MonthRow>()
  for (const delivery of deliveries) {
    rows.set(delivery.source_version, {
      version: delivery.source_version,
      month: delivery.source_month,
      delivery,
    })
  }
  for (const dataset of datasets) {
    const row = rows.get(dataset.source_version)
    rows.set(dataset.source_version, {
      version: dataset.source_version,
      month: dataset.source_month,
      delivery: row?.delivery,
      dataset,
    })
  }
  return [...rows.values()].sort((a, b) => b.version.localeCompare(a.version))
}

export function TaxRatesPage() {
  const datasets = useTaxDatasets()
  const deliveries = useTaxDeliveries()
  const importDataset = useImportTaxDataset()
  const rows = useMemo(
    () => monthRows(datasets.data ?? [], deliveries.data?.deliveries ?? []),
    [datasets.data, deliveries.data],
  )
  const active = datasets.data?.find((dataset) => dataset.status === 'active') ?? null
  const [selectedId, setSelectedId] = useState<string>()
  const preferred =
    rows.find((row) => row.dataset?.status === 'imported')?.dataset ?? active ?? undefined
  const reviewId = selectedId ?? preferred?.id

  return (
    <div className="mx-auto max-w-7xl space-y-8">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">Tax rates</h1>
        {datasets.isLoading ? (
          <Skeleton className="h-5 w-80" />
        ) : active ? (
          <p className="text-sm text-muted-foreground">
            Quotes use the{' '}
            <span className="font-medium text-foreground">{active.source_month}</span> rates for{' '}
            {formatNumber(active.row_count, 0)} ZIP codes.
          </p>
        ) : (
          <p className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
            No rates are in use. Every quote&apos;s tax needs review until a month is activated.
          </p>
        )}
      </div>

      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-8">
          <section aria-labelledby="months-heading" className="space-y-3">
            <h2 id="months-heading" className="text-base font-semibold">
              Months
            </h2>
            {deliveries.error || deliveries.data?.source_error ? (
              <p className="text-sm text-muted-foreground" role="status">
                {deliveries.data?.source_error
                  ? sentence(deliveries.data.source_error)
                  : taxErrorMessage(deliveries.error, 'The rate files could not be listed.')}{' '}
                Imported months are still shown.
              </p>
            ) : null}
            {importDataset.error ? (
              <p className="text-sm font-medium text-destructive" role="alert">
                {taxErrorMessage(importDataset.error, 'The month could not be imported. Try again.')}
              </p>
            ) : null}
            <MonthsTable
              rows={rows}
              loading={datasets.isLoading || deliveries.isLoading}
              reviewId={reviewId}
              importingVersion={importDataset.isPending ? importDataset.variables : undefined}
              onReview={setSelectedId}
              onImport={(version) =>
                importDataset.mutate(version, { onSuccess: (dataset) => setSelectedId(dataset.id) })
              }
            />
          </section>

          {reviewId ? <DatasetReview id={reviewId} activeId={active?.id ?? null} /> : null}
        </div>

        <ZipLookup />
      </div>
    </div>
  )
}

function MonthsTable({
  rows,
  loading,
  reviewId,
  importingVersion,
  onReview,
  onImport,
}: {
  rows: MonthRow[]
  loading: boolean
  reviewId?: string
  importingVersion?: string
  onReview: (id: string) => void
  onImport: (version: string) => void
}) {
  if (loading) return <Skeleton className="h-32 w-full" />
  if (!rows.length) {
    return (
      <p className="rounded-lg border px-4 py-6 text-sm text-muted-foreground">
        No rate files have been delivered yet.
      </p>
    )
  }
  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Month</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="hidden text-right sm:table-cell">ZIP codes</TableHead>
            <TableHead className="hidden sm:table-cell">Coverage</TableHead>
            <TableHead className="w-0" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const status = row.dataset ? STATUS[row.dataset.status] : null
            const selected = row.dataset && row.dataset.id === reviewId
            const missing = row.dataset?.missing_states ?? row.delivery?.missing_states ?? []
            return (
              <TableRow key={row.version} data-state={selected ? 'selected' : undefined}>
                <TableCell className="font-medium">{row.month}</TableCell>
                <TableCell>
                  {status ? (
                    <StatusPill tone={status.tone}>{status.label}</StatusPill>
                  ) : (
                    <StatusPill tone="neutral">Not imported</StatusPill>
                  )}
                </TableCell>
                <TableCell className="hidden text-right tabular-nums sm:table-cell">
                  {row.dataset ? formatNumber(row.dataset.row_count, 0) : '—'}
                </TableCell>
                <TableCell className="hidden text-muted-foreground sm:table-cell">
                  {coverageLabel(missing)}
                </TableCell>
                <TableCell className="text-right">
                  {row.dataset ? (
                    <Button
                      size="sm"
                      variant={selected ? 'secondary' : 'outline'}
                      aria-pressed={selected}
                      onClick={() => row.dataset && onReview(row.dataset.id)}
                    >
                      Review
                    </Button>
                  ) : (
                    <LoadingButton
                      size="sm"
                      loading={importingVersion === row.version}
                      loadingText="Importing…"
                      disabled={importingVersion !== undefined}
                      onClick={() => onImport(row.version)}
                    >
                      Import
                    </LoadingButton>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

function DatasetReview({ id, activeId }: { id: string; activeId: string | null }) {
  const dataset = useTaxDataset(id)
  if (dataset.isLoading) return <Skeleton className="h-48 w-full" />
  if (dataset.error || !dataset.data) {
    return (
      <p className="rounded-lg border px-4 py-6 text-sm text-muted-foreground">
        This month couldn&apos;t load. Refresh the page to try again.
      </p>
    )
  }
  return <DatasetReviewBody dataset={dataset.data} activeId={activeId} />
}

function DatasetReviewBody({
  dataset,
  activeId,
}: {
  dataset: TaxDatasetDetail
  activeId: string | null
}) {
  const activate = useActivateTaxDataset()
  const [confirming, setConfirming] = useState(false)
  const comparison = dataset.comparison
  const canActivate = dataset.status !== 'active'
  const status = STATUS[dataset.status]
  return (
    <section aria-labelledby="review-heading" className="space-y-5 rounded-xl border bg-card p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="review-heading" className="text-base font-semibold">
              {dataset.source_month}
            </h2>
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
          </div>
          <p className="text-xs text-muted-foreground">
            Imported {formatEventTime(dataset.imported_at)}
            {dataset.imported_by ? ` by ${dataset.imported_by.name}` : ''}
            {dataset.activated_at && dataset.status === 'active'
              ? ` · Activated ${formatEventTime(dataset.activated_at)}${dataset.activated_by ? ` by ${dataset.activated_by.name}` : ''}`
              : ''}
          </p>
        </div>
        {canActivate && !confirming ? (
          <Button onClick={() => setConfirming(true)}>Use these rates</Button>
        ) : null}
      </div>

      {confirming ? (
        <div className="flex flex-col gap-3 rounded-lg border bg-muted/40 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm">
            New tax calculations will use {dataset.source_month}. Saved quotes keep their rate until
            their Tax stage is saved again.
          </p>
          <div className="flex shrink-0 gap-2">
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={activate.isPending}>
              Cancel
            </Button>
            <LoadingButton
              loading={activate.isPending}
              loadingText="Activating…"
              onClick={() =>
                activate.mutate(
                  { id: dataset.id, expectedActiveId: activeId },
                  { onSuccess: () => setConfirming(false) },
                )
              }
            >
              Activate {dataset.source_month}
            </LoadingButton>
          </div>
        </div>
      ) : null}
      {activate.error ? (
        <p className="text-sm font-medium text-destructive" role="alert">
          {taxErrorMessage(activate.error, 'These rates could not be activated. Try again.')}
        </p>
      ) : null}

      {comparison?.compared_to_month ? (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Compared with {comparison.compared_to_month}</h3>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            {(
              [
                ['Changed', comparison.changed],
                ['New ZIPs', comparison.added],
                ['Removed ZIPs', comparison.removed],
                ['Unchanged', comparison.unchanged],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-3 border-b pb-1.5">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="font-medium tabular-nums">{formatNumber(value, 0)}</dd>
              </div>
            ))}
          </dl>
          {comparison.largest_changes.length ? (
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ZIP</TableHead>
                    <TableHead>Tax area</TableHead>
                    <TableHead className="text-right">Was</TableHead>
                    <TableHead className="text-right">Now</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparison.largest_changes.map((change) => (
                    <TableRow key={`${change.state_code}-${change.zip_code}`}>
                      <TableCell className="font-mono text-xs tabular-nums">{change.zip_code}</TableCell>
                      <TableCell>
                        {titleCase(change.region_name)}, {change.state_code}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatRateFraction(change.previous_rate)}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatRateFraction(change.rate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </div>
      ) : null}

      <StateFiles dataset={dataset} />
    </section>
  )
}

function StateFiles({ dataset }: { dataset: TaxDatasetDetail }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="space-y-3">
      <button
        type="button"
        className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} aria-hidden />
        {dataset.state_count} state files · {coverageLabel(dataset.missing_states)}
      </button>
      {open ? (
        <ul className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3 xl:grid-cols-4">
          {dataset.files.map((file) => (
            <li key={file.state_code} className="flex items-baseline justify-between gap-3 border-b py-1">
              <span className="truncate">{STATE_NAMES[file.state_code] ?? file.state_code}</span>
              <span className="tabular-nums text-muted-foreground">{formatNumber(file.row_count, 0)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function sentence(value: string): string {
  return /[.!?]$/.test(value) ? value : `${value}.`
}

function titleCase(value: string): string {
  return value.toLowerCase().replace(/\b[a-z]/g, (letter) => letter.toUpperCase())
}

function ZipLookup() {
  const lookup = useTaxLookup()
  const [zip, setZip] = useState('')
  const [state, setState] = useState('')
  const [city, setCity] = useState('')
  const result = lookup.data

  function submit(event: FormEvent) {
    event.preventDefault()
    lookup.mutate({ postal_code: zip, state: state || null, city: city || null, country: 'US' })
  }

  return (
    <aside aria-labelledby="lookup-heading" className="space-y-4 rounded-xl border bg-card p-4 sm:p-6 lg:sticky lg:top-6">
      <h2 id="lookup-heading" className="text-base font-semibold">
        Check an address
      </h2>
      <form className="space-y-3" onSubmit={submit}>
        <div className="grid grid-cols-[1fr_88px] gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="lookup-zip">ZIP</Label>
            <InlineFieldInput
              id="lookup-zip"
              inputMode="numeric"
              autoComplete="postal-code"
              value={zip}
              onChange={(event) => setZip(event.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lookup-state">State</Label>
            <InlineFieldInput
              id="lookup-state"
              maxLength={20}
              value={state}
              onChange={(event) => setState(event.target.value)}
              placeholder="TX"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lookup-city">City</Label>
          <InlineFieldInput id="lookup-city" value={city} onChange={(event) => setCity(event.target.value)} />
        </div>
        <LoadingButton type="submit" variant="outline" className="w-full" loading={lookup.isPending} loadingText="Checking…">
          <Search /> Check rate
        </LoadingButton>
      </form>
      {lookup.error ? (
        <p className="text-sm font-medium text-destructive" role="alert">
          The rate could not be checked. Try again.
        </p>
      ) : null}
      {result ? (
        <div className="space-y-3 border-t pt-4" role="status">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold">{result.jurisdiction}</p>
              {result.status === 'review' ? (
                <StatusPill tone="warning" className="mt-1">
                  Review needed
                </StatusPill>
              ) : null}
            </div>
            <p className="text-xl font-bold tabular-nums">{formatTaxRate(result.rate)}</p>
          </div>
          <p className="text-xs text-muted-foreground">{result.message}</p>
          {result.components?.length ? (
            <dl className="space-y-1 text-sm">
              {result.components.map((component) => (
                <div key={component.name} className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">{component.name}</dt>
                  <dd className="tabular-nums">{formatTaxRate(component.rate)}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      ) : null}
    </aside>
  )
}
